import riqApi from '@/api-factory/riq';

const TERMINAL_STATUSES = ['COMPLETED', 'PARTIALLY_COMPLETED', 'FAILED'];

const state = {
  requests: [],
  totalRequests: 0,
  currentRequest: null,
  results: [],
  totalResults: 0,
  bestRate: null,
  failedTasks: [],
  totalFailedTasks: 0,
  failedCarrierCounts: [],
  filterOptions: { carriers: [], accounts: [], services: [] },
  filterOptionsLoaded: false,
  filterOptionsLoading: false,
  failedTasksLoaded: false,
  carrierConfig: null,
  stats: null,
  statsLoading: false,
  loading: false,
  pollingTimer: null,
  filteredReports: [],
  totalFilteredReports: 0,
  filteredReportsLoading: false,
};

const actions = {
  async fetchCarrierConfig({ state, commit }) {
    if (state.carrierConfig) return;
    try {
      const response = await riqApi.getConfig();
      commit('SET_CARRIER_CONFIG', response);
    } catch (e) {
      // non-blocking — filters still work without names
    }
  },
  async fetchStats({ commit }) {
    commit('SET_STATS_LOADING', true);
    try {
      const response = await riqApi.getStats();
      commit('SET_STATS', response.data || response);
    } catch (e) {
      // non-blocking
    } finally {
      commit('SET_STATS_LOADING', false);
    }
  },

  async fetchRequests({ commit }, params = {}) {
    commit('SET_LOADING', true);
    try {
      const response = await riqApi.listRequests(params);
      commit('SET_REQUESTS', response);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchRequestDetail({ commit }, { requestId, params = {} }) {
    commit('SET_LOADING', true);
    try {
      const response = await riqApi.getRequestDetail(requestId, params);
      commit('SET_CURRENT_REQUEST', response.request);
      commit('SET_RESULTS', { data: response.results.data, total: response.results.total });
      commit('SET_FAILED_TASKS', response.failedTasks || {});
      commit('SET_BEST_RATE', response.bestRate || null);
      if (response.filterOptions) commit('SET_FILTER_OPTIONS', response.filterOptions);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchRequestHeader({ commit }, requestId) {
    try {
      const response = await riqApi.getRequestHeader(requestId);
      commit('SET_CURRENT_REQUEST', response);
      return response;
    } catch (e) {
      logger && logger.error && logger.error(e);
      throw e;
    }
  },

  async fetchRequestResults({ commit }, { requestId, params = {} }) {
    commit('SET_LOADING', true);
    try {
      const response = await riqApi.getRequestResults(requestId, params);
      commit('SET_RESULTS', { data: response.data, total: response.total });
      commit('SET_BEST_RATE', response.bestRate || null);
    } catch (e) {
      commit('SET_RESULTS', { data: [], total: 0 });
      commit('SET_BEST_RATE', null);
      throw e;
    } finally {
      commit('SET_LOADING', false);
    }
  },

    async fetchRequestFilterOptions({ commit, state }, requestId) {
      if (state.filterOptionsLoaded) return;
      commit('SET_FILTER_OPTIONS_LOADING', true);
      try {
        const response = await riqApi.getRequestFilterOptions(requestId);
        commit('SET_FILTER_OPTIONS', response);
        commit('SET_FILTER_OPTIONS_LOADED', true);
      } catch (e) {
        // non-blocking — filters still work with empty options
      } finally {
        commit('SET_FILTER_OPTIONS_LOADING', false);
      }
    },

  async fetchRequestFailedTasks({ commit, state }, { requestId, params = {} }) {
    if (state.failedTasksLoaded && !params.page && !params.carrier) return;
    const response = await riqApi.getRequestFailedTasks(requestId, params);
    commit('SET_FAILED_TASKS', response);
    commit('SET_FAILED_TASKS_LOADED', true);
  },

  async createRequest({ dispatch }, payload) {
    await riqApi.createRequest(payload);
    await dispatch('fetchRequests');
  },

  async uploadRequest({ dispatch }, formData) {
    await riqApi.uploadRequest(formData);
    await dispatch('fetchRequests');
  },

  async deleteRequest({ dispatch }, requestId) {
    await riqApi.deleteRequest(requestId);
    await dispatch('fetchRequests');
  },

  async exportRequest(_, requestId) {
    const response = await riqApi.exportRequest(requestId);
    return response;
  },

  async createFilteredExport(_, { requestId, filters }) {
    const response = await riqApi.createFilteredExport(requestId, filters);
    console.log('[RIQ-STORE] createFilteredExport response:', response);
    return response;
  },

  async fetchFilteredReports({ commit }, { requestId, params = {} }) {
    commit('SET_FILTERED_REPORTS_LOADING', true);
    try {
      const response = await riqApi.getFilteredReports(requestId, params);
      if (params.page && params.page > 1) {
        commit('APPEND_FILTERED_REPORTS', response);
      } else {
        commit('SET_FILTERED_REPORTS', response);
      }
    } finally {
      commit('SET_FILTERED_REPORTS_LOADING', false);
    }
  },

  async downloadFilteredReport(_, { requestId, reportId }) {
    const response = await riqApi.downloadFilteredReport(requestId, reportId);
    return response;
  },

  async downloadTemplate() {
    var response = await riqApi.downloadTemplate();
    return response;
  },

  async getInvalidEntries(_, { requestId, params = {} }) {
    const response = await riqApi.getInvalidEntries(requestId, params);
    return response;
  },

  async getInvalidDownloadUrl(_, requestId) {
    const response = await riqApi.getInvalidDownloadUrl(requestId);
    return response;
  },

  startPolling({ dispatch, state, commit }, params = {}) {
    if (state.pollingTimer) return;
    async function poll() {
      try {
        await dispatch('fetchRequests', params);
      } catch (e) {
        // non-blocking
      }
      if (state.pollingTimer) {
        commit('SET_POLLING_TIMER', setTimeout(poll, 10000));
      }
    }
    commit('SET_POLLING_TIMER', setTimeout(poll, 10000));
  },

  stopPolling({ state, commit }) {
    if (state.pollingTimer) {
      clearTimeout(state.pollingTimer);
      commit('SET_POLLING_TIMER', null);
    }
  },

  resetDetailState({ commit }) {
    commit('SET_CURRENT_REQUEST', null);
    commit('SET_RESULTS', { data: [], total: 0 });
    commit('SET_BEST_RATE', null);
    commit('SET_FAILED_TASKS', { data: [], total: 0, carrierCounts: [] });
    commit('SET_FILTER_OPTIONS', { carriers: [], accounts: [], services: [] });
    commit('SET_FILTER_OPTIONS_LOADED', false);
    commit('SET_FAILED_TASKS_LOADED', false);
    commit('SET_FILTERED_REPORTS', { data: [], total: 0 });
  },
};

const mutations = {
  SET_REQUESTS(state, payload) {
    state.requests = Object.freeze((payload && Array.isArray(payload.data) ? payload.data : payload) || []);
    state.totalRequests = (payload && payload.total) || 0;
  },
  SET_CURRENT_REQUEST(state, request) {
    state.currentRequest = request;
  },
  SET_RESULTS(state, { data, total }) {
    state.results = Object.freeze(data);
    state.totalResults = total;
  },
  SET_FAILED_TASKS(state, payload) {
    if (payload && Array.isArray(payload.data)) {
      state.failedTasks = Object.freeze(payload.data);
      state.totalFailedTasks = payload.total || 0;
      state.failedCarrierCounts = Object.freeze(payload.carrierCounts || []);
    } else {
      state.failedTasks = Object.freeze(Array.isArray(payload) ? payload : []);
      state.totalFailedTasks = state.failedTasks.length;
      state.failedCarrierCounts = [];
    }
  },
  SET_BEST_RATE(state, bestRate) {
    state.bestRate = bestRate;
  },
  SET_CARRIER_CONFIG(state, config) {
    state.carrierConfig = config;
  },
  SET_STATS(state, stats) {
    state.stats = stats;
  },
  SET_STATS_LOADING(state, val) {
    state.statsLoading = val;
  },
  SET_FILTER_OPTIONS(state, options) {
    state.filterOptions = options;
  },
  SET_FILTER_OPTIONS_LOADED(state, val) {
    state.filterOptionsLoaded = val;
  },
  SET_FILTER_OPTIONS_LOADING(state, val) {
    state.filterOptionsLoading = val;
  },
  SET_FAILED_TASKS_LOADED(state, val) {
    state.failedTasksLoaded = val;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_POLLING_TIMER(state, timer) {
    state.pollingTimer = timer;
  },
  SET_FILTERED_REPORTS(state, payload) {
    state.filteredReports = payload.data || [];
    state.totalFilteredReports = payload.total || 0;
  },
  APPEND_FILTERED_REPORTS(state, payload) {
    state.filteredReports = state.filteredReports.concat(payload.data || []);
    state.totalFilteredReports = payload.total || 0;
  },
  SET_FILTERED_REPORTS_LOADING(state, val) {
    state.filteredReportsLoading = val;
  },
};

const getters = {
  hasProcessingJobs: state => Array.isArray(state.requests) && state.requests.some(r => r.status === 'PROCESSING' || r.status === 'SPLITTING' || r.status === 'PENDING'),
  progressPct: state => (state.currentRequest && state.currentRequest.progress_pct) || 0,
  isTerminal: state => state.currentRequest && TERMINAL_STATUSES.includes(state.currentRequest.status),

};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
